import type { AxisAssessment } from "@shared/labs/level-assessment";
import { ArrowDown, ArrowUp, Ban, Check, Layers, Minus, SearchX, Target, Zap } from "lucide-react";
import { scrubEngineTokensClient } from "../candidates/scrub-engine-tokens";
import { DecisiveSignals } from "./critical-signals";
import { axisWhyData } from "./derive";
import { DetailShell, FieldBlock } from "./detail-shell";
import { AllSignals } from "./signals-used";
import { C, DM, UI } from "./ui-tokens";
import type { WhyData } from "./why-panel";

export interface AxisDetailProps {
  assessment: AxisAssessment;
  axisLabel: string;
  levels: { min: number; max: number };
  /** Candidate LinkedIn URL — powers the "Abrir no LinkedIn" link on a decisive
   *  signal's citation. Absent on free-text / demo runs. */
  linkedinUrl?: string | null;
  onClose: () => void;
}

/** One step of the level-decision ladder (below · held · above). */
function LadderStep({
  level,
  tag,
  icon: Icon,
  held,
}: {
  level: string;
  tag: string;
  icon: typeof Check;
  held?: boolean;
}) {
  return (
    <div
      style={{
        flex: 1,
        border: `1px solid ${held ? C.red : C.border}`,
        borderRadius: 10,
        padding: "11px 14px",
        background: held ? C.red5 : C.paper,
      }}
    >
      <div
        style={{
          ...DM,
          fontSize: 17,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          color: held ? C.red : C.ink,
        }}
      >
        {level}
      </div>
      <div
        style={{
          ...UI,
          fontSize: 11,
          fontWeight: 600,
          color: held ? C.red : C.ink50,
          marginTop: 1,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <Icon size={13} /> {tag}
      </div>
    </div>
  );
}

/** One lane of the contestability panel (why it cleared the floor / holds /
 *  doesn't reach the ceiling). */
function Lane({
  head,
  headColor,
  icon: Icon,
  items,
  last,
}: {
  head: string;
  headColor: string;
  icon: typeof Check;
  items: WhyData["satisfied"]["items"];
  last?: boolean;
}) {
  return (
    <div style={{ padding: "14px 16px", borderRight: last ? "none" : `1px solid ${C.border}` }}>
      <div
        style={{
          ...UI,
          fontSize: 12,
          fontWeight: 700,
          color: headColor,
          display: "flex",
          alignItems: "center",
          gap: 7,
          marginBottom: 11,
        }}
      >
        <Icon size={14} /> {head}
      </div>
      {items.length === 0 ? (
        <div style={{ ...UI, fontSize: 12, color: C.ink50 }}>—</div>
      ) : (
        items.map((r) => (
          <div key={r.point} style={{ marginBottom: 13 }}>
            <div style={{ ...UI, fontSize: 13, fontWeight: 600, color: C.ink, lineHeight: 1.4 }}>
              {r.point}
            </div>
            <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 3, lineHeight: 1.45 }}>
              {r.evidence}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** The level decision: a 3-step ladder (cleared · held · ceiling) over the
 *  3-lane contestability panel — both driven by the per-axis why-fields. */
function LevelDecision({ why }: { why: WhyData }) {
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <LadderStep level={why.down.level} tag="superou" icon={Check} />
        <LadderStep level={why.satisfied.level} tag="nível definido" icon={Target} held />
        <LadderStep level={why.up.level} tag="teto" icon={Ban} />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Lane
          head={`Por que superou ${why.down.level}`}
          headColor={C.green}
          icon={ArrowUp}
          items={why.down.items}
        />
        <Lane
          head={`Por que é ${why.satisfied.level}`}
          headColor={C.red}
          icon={Target}
          items={why.satisfied.items}
        />
        <Lane
          head={`Por que não chega a ${why.up.level}`}
          headColor={C.amberFg}
          icon={ArrowDown}
          items={why.up.items}
          last
        />
      </div>
    </>
  );
}

/** Expanded reading of one competency: the matrix excerpt for the level, the
 *  level decision (ladder + contestability lanes), the decisive signals with
 *  their profile citation, and the full signal list. */
export function AxisDetail({
  assessment,
  axisLabel,
  levels,
  linkedinUrl,
  onClose,
}: AxisDetailProps) {
  const nd = assessment.inferredLevel === null;
  const why = axisWhyData(assessment, levels);
  const signals = assessment.signalsUsed ?? [];
  const criticalSignals = assessment.criticalSignals ?? [];
  // The explainer-authored summary is the user-facing axis narrative (already
  // voice-gated at synthesis time — never scrubbed here). Rows produced before
  // the explainer shipped, or a fused axis whose S1 call degraded, fall back
  // to the scoring `reasoning` — an AUDIT field whose prompt contract cites
  // claim/point ids (`docs/labs/level-assessment/multi-round-narrative-layer.md`
  // §5). Client-side scrub keeps that historical fallback display-only clean.
  const narrative =
    assessment.explanation?.summary || scrubEngineTokensClient(assessment.reasoning);

  return (
    <DetailShell
      title={axisLabel}
      badge="Detalhe da competência"
      level={nd ? null : `L${assessment.inferredLevel}`}
      nd={nd}
      confidence={assessment.confidence}
      onClose={onClose}
    >
      {narrative && (
        <p style={{ ...UI, fontSize: 14, color: C.ink70, lineHeight: 1.55, margin: 0 }}>
          {narrative}
        </p>
      )}

      {assessment.bandStatus && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 10,
            background: assessment.bandStatus === "below" ? C.amberBg : C.greenBg,
            border: `1px solid ${assessment.bandStatus === "below" ? C.amberFg : C.greenFg}`,
          }}
        >
          <p
            style={{
              ...UI,
              fontSize: 13,
              fontWeight: 600,
              color: assessment.bandStatus === "below" ? C.amberFg : C.greenFg,
              margin: 0,
            }}
          >
            {assessment.bandStatus === "below"
              ? "Abaixo da faixa desta função"
              : "Acima da faixa desta função"}
          </p>
          <p style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5, margin: "6px 0 0" }}>
            {assessment.bandStatus === "below"
              ? "Os sinais ficam aquém do nível inicial que esta função exige nesta competência. O nível mostrado é um degrau abaixo do piso da faixa e reduz o resultado geral."
              : "Os sinais superam o teto que esta função define nesta competência. O nível mostrado é um degrau acima do topo da faixa."}
          </p>
        </div>
      )}

      {nd ? (
        <FieldBlock label="Qual evidência faltou" icon={SearchX}>
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 10,
              background: C.ink5,
              border: `1px dashed ${C.ink20}`,
            }}
          >
            {assessment.missingEvidence.length === 0 ? (
              <span style={{ ...UI, fontSize: 13, color: C.ink70 }}>
                Evidência insuficiente para estimar o nível com confiança.
              </span>
            ) : (
              assessment.missingEvidence.map((m) => (
                <div
                  key={m}
                  style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "4px 0" }}
                >
                  <span style={{ marginTop: 1, flex: "none", display: "inline-flex" }}>
                    <Minus size={15} color={C.ink50} />
                  </span>
                  <span style={{ ...UI, fontSize: 13, color: C.ink70, lineHeight: 1.5 }}>{m}</span>
                </div>
              ))
            )}
          </div>
          <p
            style={{ ...UI, fontSize: 12.5, color: C.ink50, lineHeight: 1.5, margin: "10px 2px 0" }}
          >
            N/D não significa nível baixo: a confiança ({Math.round(assessment.confidence * 100)}%)
            ficou abaixo do mínimo. Esta competência não entra na estimativa geral.
          </p>
        </FieldBlock>
      ) : (
        <>
          {assessment.levelRubric && (
            <FieldBlock label={`O que L${assessment.inferredLevel} exige`} icon={Target}>
              <p
                style={{
                  ...UI,
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: C.ink,
                  margin: 0,
                  padding: "12px 14px",
                  background: C.paper,
                  borderRadius: 10,
                  borderLeft: `3px solid ${C.red}`,
                }}
              >
                {assessment.levelRubric}
              </p>
            </FieldBlock>
          )}

          {why && (
            <FieldBlock label="A decisão de nível" icon={Layers}>
              {assessment.ruleApplied && (
                <p
                  style={{
                    ...UI,
                    fontSize: 13,
                    color: C.ink50,
                    lineHeight: 1.5,
                    margin: "0 0 14px",
                    paddingLeft: 13,
                    borderLeft: `2px solid ${C.border}`,
                  }}
                >
                  <b style={{ color: C.ink, fontWeight: 600 }}>Regra aplicada.</b>{" "}
                  {assessment.ruleApplied}
                </p>
              )}
              <LevelDecision why={why} />
            </FieldBlock>
          )}

          {criticalSignals.length > 0 && (
            <FieldBlock label="Sinais decisivos · fonte no perfil" icon={Zap}>
              <DecisiveSignals signals={criticalSignals} linkedinUrl={linkedinUrl} />
            </FieldBlock>
          )}
        </>
      )}

      {signals.length > 0 && <AllSignals signals={signals} />}
    </DetailShell>
  );
}
